import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {Box, ButtonBase} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import React from "react";
import {signingControls} from "./signingColors";
import {cardRadius, displayFontFamily} from "./signingTheme";
import {PAD_INK, PAD_PAPER} from "./signaturePad.util";

export const BOX_LABEL = '#66615A';
const BOX_PLACEHOLDER = '#8F8A82';
const BOX_ACTION = '#4A453E';
const BOX_BASELINE = '#D6D0C4';

export const SIGNATURE_BOX_HEIGHT = {xs: 150, sm: 180};
const SIGNATURE_FOOT = 44;

export function SignatureBox({touchLock = true, children}: {touchLock?: boolean, children: React.ReactNode}) {
    const theme = useTheme();
    const controls = signingControls(theme);

    return (
        <Box
            sx={{
                position: 'relative',
                height: SIGNATURE_BOX_HEIGHT,
                borderRadius: `${cardRadius(theme)}px`,
                border: '1px solid',
                borderColor: controls.border,
                bgcolor: PAD_PAPER,
                color: PAD_INK,
                overflow: 'hidden',
                ...(touchLock && {
                    touchAction: 'none',
                    overscrollBehavior: 'none',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    WebkitTouchCallout: 'none',
                }),
                transition: 'border-color 160ms, box-shadow 160ms',
                '&:focus-within': {borderColor: controls.focus, boxShadow: `0 0 0 1px ${controls.focus}`},
            }}
        >
            {children}
        </Box>
    );
}

export function SignatureBaseline() {
    return (
        <Box
            aria-hidden
            sx={{position: 'absolute', left: 20, right: 20, bottom: SIGNATURE_FOOT, borderBottom: `1px solid ${BOX_BASELINE}`}}
        />
    );
}

export function SignaturePlaceholder({text, hidden}: {text: string, hidden: boolean}) {
    const theme = useTheme();

    return (
        <Box
            aria-hidden
            sx={{
                position: 'absolute',
                inset: `0 16px ${SIGNATURE_FOOT}px`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
                opacity: hidden ? 0 : 1,
                transition: 'opacity 220ms cubic-bezier(0.22, 1, 0.36, 1)',
            }}
        >
            <Typography sx={{fontFamily: displayFontFamily(theme), fontStyle: 'italic', fontSize: {xs: 24, sm: 26}, color: BOX_PLACEHOLDER}}>
                {text}
            </Typography>
        </Box>
    );
}

export function SignatureFoot({label, action}: {label: string, action?: React.ReactNode}) {
    return (
        <Box
            sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                height: SIGNATURE_FOOT,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                pl: 2.5,
                pr: 0.5,
                pointerEvents: 'none',
            }}
        >
            <Typography component={'span'} variant={'caption'} sx={{color: BOX_LABEL}}>
                {label}
            </Typography>
            <Box sx={{pointerEvents: 'auto'}}>{action}</Box>
        </Box>
    );
}

export function SignatureAction({icon, hidden, disabled, onClick, children}: {
    icon?: React.ReactNode;
    hidden?: boolean;
    disabled?: boolean;
    onClick: () => void;
    children: React.ReactNode;
}) {
    return (
        <ButtonBase
            onClick={onClick}
            disabled={disabled}
            sx={{
                height: SIGNATURE_FOOT,
                px: 1.5,
                gap: 0.75,
                borderRadius: 1,
                color: BOX_ACTION,
                fontSize: 14,
                fontWeight: 600,
                visibility: hidden ? 'hidden' : 'visible',
                opacity: hidden ? 0 : disabled ? 0.5 : 1,
                transition: 'opacity 180ms ease-out, visibility 180ms',
                '@media (hover: hover)': {'&:hover': {bgcolor: 'rgba(0, 0, 0, 0.04)'}},
                '&:active': {bgcolor: 'rgba(0, 0, 0, 0.06)'},
                '&.Mui-focusVisible': {outline: `2px solid ${PAD_INK}`, outlineOffset: -4},
            }}
        >
            {icon}
            {children}
        </ButtonBase>
    );
}

export function SignatureNotice({message, attention}: {message?: string, attention?: boolean}) {
    return (
        <Box
            role={'status'}
            sx={{position: 'absolute', top: 10, left: 12, right: 12, display: 'flex', justifyContent: 'center', pointerEvents: 'none'}}
        >
            {!!message && (
                <Typography
                    variant={'caption'}
                    sx={{
                        px: 1.25,
                        py: 0.5,
                        borderRadius: 999,
                        textAlign: 'center',
                        color: attention ? '#7A4A0B' : BOX_ACTION,
                        bgcolor: attention ? '#FFF4E0' : 'rgba(255, 255, 255, 0.94)',
                        boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.06)',
                    }}
                >
                    {message}
                </Typography>
            )}
        </Box>
    );
}

export function SignatureLock({message}: {message: string}) {
    return (
        <Box
            sx={{
                position: 'absolute',
                inset: `0 16px ${SIGNATURE_FOOT}px`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                px: 1,
                textAlign: 'center',
            }}
        >
            <LockOutlinedIcon aria-hidden sx={{fontSize: 20, color: BOX_LABEL, flexShrink: 0}}/>
            <Typography variant={'body1'} sx={{color: BOX_ACTION, fontWeight: 500, textWrap: 'balance'}}>
                {message}
            </Typography>
        </Box>
    );
}
